import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Select, IProps as SelectProps } from 'components/atoms/Select/Select.components';
import { useGetPicklistQuery } from 'services/sap';
import { formatCountrySelect } from 'utils/helpers';
import { catalogoLenguage, catalogoPersonal } from 'utils/helpers/dictionaries';

interface DepartmentSelectProps extends Omit<SelectProps, 'options' | 'handleChange'> {
  country: string;
  onExternalCodeChange: (externalCode: string) => void;
  handleChange: (value?: string, externalCode?: string) => void;
  onError?: (error: boolean | null) => void;
  error?: boolean;
  name?: any;
  catalogName?: string;
  defaultValue?: string;
}

export default function DepartmentSelect({
  country,
  onExternalCodeChange,
  onError,
  name,
  defaultValue = '',
  catalogName = catalogoPersonal.REGION_COL,
  ...rest
}: Readonly<DepartmentSelectProps>) {
  const { getValues } = useFormContext();
  const { refetch, data, isLoading, isError } = useGetPicklistQuery<{
    data: { externalCode: string; label_es_ES: string; optionId: string; status: string }[];
    isLoading: boolean;
    isError: boolean;
  }>({
    catalogName,
    languages: catalogoLenguage.ESyUSyISOyId,
    father: catalogName === catalogoPersonal.REGION_COL ? country : undefined,
  });

  const [initialLoading, setInitialLoading] = useState(false);

  const options = formatCountrySelect({ initialList: data ?? [], isStateOrCity: true });

  useEffect(() => {
    if (catalogName && country) refetch();
  }, [catalogName, country]);

  useEffect(() => {
    const formValue = getValues(name);
    if (!data || isLoading || !defaultValue || initialLoading) {
      return;
    }
    let state;

    if (formValue === defaultValue) {
      state = data?.find((dept) => dept.label_es_ES === defaultValue);
    }

    if (!state) {
      return;
    }
    onExternalCodeChange(state?.externalCode ?? '');
    rest.handleChange(defaultValue ?? '', state?.externalCode ?? '');

    setInitialLoading(true);
  }, [isLoading, data]);

  return (
    <Select
      options={options}
      {...rest}
      disabled={rest.disabled || isError}
      handleChange={(value) => {
        let department;

        if (value) {
          department = data?.find((dept) => dept.label_es_ES === value);
        }

        onExternalCodeChange(department?.externalCode ?? '');

        rest.handleChange(value?.toString() ?? '', department?.externalCode ?? '');
      }}
    />
  );
}
